

function filterQueryTokens(tokens) {
  return tokens.filter(t => !BANNED_QUERY_TOKENS.has(t));
}

const MIN_TOKEN_LEN = 3;

// ---------- helpers ----------
function normalizeText(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")       // remove accents
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s,_-]+/g, " ")      // keep commas/underscores/hyphens for phrase parsing
    .replace(/\s+/g, " ")
    .trim();
}



function tokenize(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .map(t => t.trim())
    .filter(t => t.length >= MIN_TOKEN_LEN && !STOPWORDS.has(t));
}

/**
 * Parse metafield like: "cereal aisle, endcap, price sign"
 * Returns:
 *  - uniSet: unigrams + phrase bigrams (price_sign)
 *  - biSet: only bigrams (word1_word2)
 */
function parseKeywordsToTokenSets(keywordString) {
  const raw = normalizeText(keywordString);
  const phrases = raw
    .split(",")
    .map(p => p.trim())
    .filter(Boolean);

  const uniSet = new Set();
  const biSet = new Set();

  for (const p of phrases) {
    const words = normalizeText(p)
      .replace(/[_-]/g, " ")
      .split(" ")
      .map(w => w.trim())
      .filter(w => w.length >= MIN_TOKEN_LEN && !STOPWORDS.has(w));

    // unigrams
    for (const w of words) uniSet.add(w);

    // bigrams
    for (let i = 0; i < words.length - 1; i++) {
      const bg = `${words[i]}_${words[i+1]}`;
      uniSet.add(bg); // allow phrase signal in main set too
      biSet.add(bg);
    }
  }

  return { uniSet, biSet };
}

function buildIdfMap(items, getUniSet) {
  const df = new Map();
  const N = items.length;

  for (const item of items) {
    const set = getUniSet(item);
    for (const t of set) df.set(t, (df.get(t) || 0) + 1);
  }

  const idf = new Map();
  for (const [t, d] of df.entries()) {
    // smoothed idf; +1 keeps it stable on small candidate sets
    idf.set(t, Math.log((N + 1) / (d + 1)) + 1);
  }
  return idf;
}

function weightedJaccard(aSet, bSet, wMap) {
  if (!aSet.size || !bSet.size) return 0;

  let inter = 0;
  let uni = 0;

  // union weights: iterate over bigger union safely by marking
  const seen = new Set();

  for (const t of aSet) {
    const w = wMap.get(t) || 1;
    uni += w;
    seen.add(t);
    if (bSet.has(t)) inter += w;
  }

  for (const t of bSet) {
    if (seen.has(t)) continue;
    const w = wMap.get(t) || 1;
    uni += w;
  }

  return uni ? (inter / uni) : 0;
}

// Optional: penalize matches where overlap is only super-common tokens
function genericOverlapPenalty(aSet, bSet, idfMap, threshold = 1.15) {
  // threshold ~ tokens that appear in most candidates will have low idf (close to 1)
  let hasSpecific = false;
  for (const t of aSet) {
    if (bSet.has(t) && (idfMap.get(t) || 1) > threshold) {
      hasSpecific = true;
      break;
    }
  }
  return hasSpecific ? 0 : 0.08; // tune
}

// ---------- main scorer ----------
function scoreRelated(base, candidate, idfUni, idfBi) {
  const uni = weightedJaccard(base.uniSet, candidate.uniSet, idfUni);
  const bi  = weightedJaccard(base.biSet,  candidate.biSet,  idfBi);

  // Small, optional boosts (only if you have these fields)
  let boost = 0;
  if (base.productType && candidate.productType && base.productType === candidate.productType) boost += 0.03;
  if (base.vendor && candidate.vendor && base.vendor === candidate.vendor) boost += 0.02;

  const penalty = genericOverlapPenalty(base.uniSet, candidate.uniSet, idfUni);

  return (0.70 * uni) + (0.25 * bi) + boost - penalty;
}

/**
 * candidates: array of products with { keywords, productType, vendor, ... }
 * baseProduct: same shape
 */
function rankRelated(baseProduct, candidates) {
  const baseSets = parseKeywordsToTokenSets(baseProduct.keywords);

  // precompute token sets for candidates once
  const enriched = candidates.map(p => ({
    ...p,
    _sets: parseKeywordsToTokenSets(p.keywords)
  }));

  const idfUni = buildIdfMap(enriched, (x) => x._sets.uniSet);
  const idfBi  = buildIdfMap(enriched, (x) => x._sets.biSet);

  const base = {
    ...baseProduct,
    ...baseSets
  };

  return enriched
    .map(p => ({
      product: p,
      score: scoreRelated(
        { ...base, productType: baseProduct.productType, vendor: baseProduct.vendor },
        { ...p._sets, productType: p.productType, vendor: p.vendor },
        idfUni,
        idfBi
      )
    }))
    .sort((a, b) => b.score - a.score);
}